import { type TFunction } from 'react-i18next';
import { type GetCompanySpecsForNewModuleQuery } from '../../../api/queries/getCompanySpecsForNewModule';
import { ModuleType } from '../../../api/types';
import { implementations } from '../ModuleDetailsPage/modules';
import { type ModuleImplementation } from '../ModuleDetailsPage/modules/implementations/types';

export type CompanySpecs = GetCompanySpecsForNewModuleQuery['company'];

type Implementations = typeof implementations;
type ImplementationKeys = keyof Implementations;

export type ModuleClass = {
    type: ImplementationKeys;
    label: string;
    description: React.ReactNode | JSX.Element;
    group: string;
    FormComponent: React.FC<{ company: CompanySpecs }>;
    create: ModuleImplementation<any, any>['create'];
    getValidator: ModuleImplementation<any, any>['getValidator'];
    disabled?: boolean;
    initialValues?: Implementations[ImplementationKeys]['initialValues'] | null;
};

const getAvailableModules = (company: CompanySpecs, t: TFunction) => {
    const results: ModuleClass[] = [];

    results.push({
        type: 'ConsentsAndDeclarationsModule',
        label: t('moduleDetails:implementations.ConsentsAndDeclarationsModule.className'),
        description: t('moduleDetails:implementations.ConsentsAndDeclarationsModule.description'),
        group: t('moduleDetails:implementations.ConsentsAndDeclarationsModule.group'),
        FormComponent: implementations.ConsentsAndDeclarationsModule.FormComponent,
        create: implementations.ConsentsAndDeclarationsModule.create,
        getValidator: implementations.ConsentsAndDeclarationsModule.getValidator,
    });

    if (!company.modules.some(module => module.__typename === 'SimpleVehicleManagementModule')) {
        results.push({
            type: 'SimpleVehicleManagementModule',
            label: t('moduleDetails:implementations.SimpleVehicleManagementModule.className'),
            description: t('moduleDetails:implementations.SimpleVehicleManagementModule.description'),
            group: t('moduleDetails:implementations.SimpleVehicleManagementModule.group'),
            FormComponent: implementations.SimpleVehicleManagementModule.FormComponent,
            create: implementations.SimpleVehicleManagementModule.create,
            getValidator: implementations.SimpleVehicleManagementModule.getValidator,
            initialValues: implementations.SimpleVehicleManagementModule.initialValues,
        });
    }

    results.push({
        type: 'AppointmentModule',
        label: t('moduleDetails:implementations.AppointmentModule.className'),
        description: t('moduleDetails:implementations.AppointmentModule.description'),
        group: t('moduleDetails:implementations.AppointmentModule.group'),
        FormComponent: implementations.AppointmentModule.FormComponent,
        create: implementations.AppointmentModule.create,
        getValidator: implementations.AppointmentModule.getValidator,
        initialValues: implementations.AppointmentModule.initialValues,
    });

    results.push({
        type: 'LocalCustomerManagementModule',
        label: t('moduleDetails:implementations.LocalCustomerManagementModule.className'),
        description: t('moduleDetails:implementations.LocalCustomerManagementModule.description'),
        group: t('moduleDetails:implementations.LocalCustomerManagementModule.group'),
        FormComponent: implementations.LocalCustomerManagementModule.FormComponent,
        create: implementations.LocalCustomerManagementModule.create,
        getValidator: implementations.LocalCustomerManagementModule.getValidator,
    });

    if (!company.modules.some(module => module.__typename === ModuleType.BankModule)) {
        results.push({
            type: 'BankModule',
            label: t('moduleDetails:implementations.BankModule.className'),
            description: t('moduleDetails:implementations.BankModule.description'),
            group: t('moduleDetails:implementations.BankModule.group'),
            FormComponent: implementations.BankModule.FormComponent,
            create: implementations.BankModule.create,
            getValidator: implementations.BankModule.getValidator,
        });
    }

    results.push({
        type: 'InsuranceModule',
        label: t('moduleDetails:implementations.InsuranceModule.className'),
        description: t('moduleDetails:implementations.InsuranceModule.description'),
        group: t('moduleDetails:implementations.InsuranceModule.group'),
        FormComponent: implementations.InsuranceModule.FormComponent,
        create: implementations.InsuranceModule.create,
        getValidator: implementations.InsuranceModule.getValidator,
    });

    results.push({
        type: 'BasicSigningModule',
        label: t('moduleDetails:implementations.BasicSigningModule.className'),
        description: t('moduleDetails:implementations.BasicSigningModule.description'),
        group: t('moduleDetails:implementations.BasicSigningModule.group'),
        FormComponent: implementations.BasicSigningModule.FormComponent,
        create: implementations.BasicSigningModule.create,
        getValidator: implementations.BasicSigningModule.getValidator,
    });

    results.push({
        type: 'NamirialSigningModule',
        label: t('moduleDetails:implementations.NamirialSigningModule.className'),
        description: t('moduleDetails:implementations.NamirialSigningModule.description'),
        group: t('moduleDetails:implementations.NamirialSigningModule.group'),
        FormComponent: implementations.NamirialSigningModule.FormComponent,
        create: implementations.NamirialSigningModule.create,
        getValidator: implementations.NamirialSigningModule.getValidator,
    });

    results.push({
        type: 'StandardApplicationModule',
        label: t('moduleDetails:implementations.StandardApplicationModule.className'),
        description: t('moduleDetails:implementations.StandardApplicationModule.description'),
        group: t('moduleDetails:implementations.StandardApplicationModule.group'),
        FormComponent: implementations.StandardApplicationModule.FormComponent,
        create: implementations.StandardApplicationModule.create,
        getValidator: implementations.StandardApplicationModule.getValidator,
        initialValues: implementations.StandardApplicationModule.initialValues,
    });

    results.push({
        type: 'EventApplicationModule',
        label: t('moduleDetails:implementations.EventApplicationModule.className'),
        description: t('moduleDetails:implementations.EventApplicationModule.description'),
        group: t('moduleDetails:implementations.EventApplicationModule.group'),
        FormComponent: implementations.EventApplicationModule.FormComponent,
        create: implementations.EventApplicationModule.create,
        getValidator: implementations.EventApplicationModule.getValidator,
        initialValues: implementations.EventApplicationModule.initialValues,
    });

    results.push({
        type: 'AdyenPaymentModule',
        label: t('moduleDetails:implementations.AdyenPaymentModule.className'),
        description: t('moduleDetails:implementations.AdyenPaymentModule.description'),
        group: t('moduleDetails:implementations.AdyenPaymentModule.group'),
        FormComponent: implementations.AdyenPaymentModule.FormComponent,
        create: implementations.AdyenPaymentModule.create,
        getValidator: implementations.AdyenPaymentModule.getValidator,
    });

    results.push({
        type: 'WhatsappLiveChatModule',
        label: t('moduleDetails:implementations.WhatsappLiveChatModule.className'),
        description: t('moduleDetails:implementations.WhatsappLiveChatModule.description'),
        group: t('moduleDetails:implementations.WhatsappLiveChatModule.group'),
        FormComponent: implementations.WhatsappLiveChatModule.FormComponent,
        create: implementations.WhatsappLiveChatModule.create,
        getValidator: implementations.WhatsappLiveChatModule.getValidator,
    });

    results.push({
        type: 'UserlikeChatbotModule',
        label: t('moduleDetails:implementations.UserlikeChatbotModule.className'),
        description: t('moduleDetails:implementations.UserlikeChatbotModule.description'),
        group: t('moduleDetails:implementations.UserlikeChatbotModule.group'),
        FormComponent: implementations.UserlikeChatbotModule.FormComponent,
        create: implementations.UserlikeChatbotModule.create,
        getValidator: implementations.UserlikeChatbotModule.getValidator,
    });

    results.push({
        type: 'MyInfoModule',
        label: t('moduleDetails:implementations.MyInfoModule.className'),
        description: t('moduleDetails:implementations.MyInfoModule.description'),
        group: t('moduleDetails:implementations.MyInfoModule.group'),
        FormComponent: implementations.MyInfoModule.FormComponent,
        create: implementations.MyInfoModule.create,
        getValidator: implementations.MyInfoModule.getValidator,
    });

    results.push({
        type: 'ConfiguratorModule',
        label: t('moduleDetails:implementations.ConfiguratorModule.className'),
        description: t('moduleDetails:implementations.ConfiguratorModule.description'),
        group: t('moduleDetails:implementations.ConfiguratorModule.group'),
        FormComponent: implementations.ConfiguratorModule.FormComponent,
        create: implementations.ConfiguratorModule.create,
        getValidator: implementations.ConfiguratorModule.getValidator,
        initialValues: implementations.ConfiguratorModule.initialValues,
    });

    results.push({
        type: 'PromoCodeModule',
        label: t('moduleDetails:implementations.PromoCodeModule.className'),
        description: t('moduleDetails:implementations.PromoCodeModule.description'),
        group: t('moduleDetails:implementations.PromoCodeModule.group'),
        FormComponent: implementations.PromoCodeModule.FormComponent,
        create: implementations.PromoCodeModule.create,
        getValidator: implementations.PromoCodeModule.getValidator,
    });

    results.push({
        type: 'SdmModule',
        label: t('moduleDetails:implementations.SdmModule.className'),
        description: t('moduleDetails:implementations.SdmModule.description'),
        group: t('moduleDetails:implementations.SdmModule.group'),
        FormComponent: implementations.SdmModule.FormComponent,
        create: implementations.SdmModule.create,
        getValidator: implementations.SdmModule.getValidator,
    });

    results.push({
        type: 'AudiFinancingModule',
        label: t('moduleDetails:implementations.AudiFinancingModule.className'),
        description: t('moduleDetails:implementations.AudiFinancingModule.description'),
        group: t('moduleDetails:implementations.AudiFinancingModule.group'),
        FormComponent: implementations.AudiFinancingModule.FormComponent,
        create: implementations.AudiFinancingModule.create,
        getValidator: implementations.AudiFinancingModule.getValidator,
    });

    results.push({
        type: 'WebsiteModule',
        label: t('moduleDetails:implementations.WebsiteModule.className'),
        description: t('moduleDetails:implementations.WebsiteModule.description'),
        group: t('moduleDetails:implementations.WebsiteModule.group'),
        FormComponent: implementations.WebsiteModule.FormComponent,
        create: implementations.WebsiteModule.create,
        getValidator: implementations.WebsiteModule.getValidator,
    });

    results.push({
        type: 'MobilityModule',
        label: t('moduleDetails:implementations.MobilityModule.className'),
        description: t('moduleDetails:implementations.MobilityModule.description'),
        group: t('moduleDetails:implementations.MobilityModule.group'),
        FormComponent: implementations.MobilityModule.FormComponent,
        create: implementations.MobilityModule.create,
        getValidator: implementations.MobilityModule.getValidator,
        initialValues: implementations.MobilityModule.initialValues,
    });

    results.push({
        type: 'LabelsModule',
        label: t('moduleDetails:implementations.LabelsModule.className'),
        description: t('moduleDetails:implementations.LabelsModule.description'),
        group: t('moduleDetails:implementations.LabelsModule.group'),
        FormComponent: implementations.LabelsModule.FormComponent,
        create: implementations.LabelsModule.create,
        getValidator: implementations.LabelsModule.getValidator,
    });

    results.push({
        type: 'BmwModule',
        label: t('moduleDetails:implementations.BmwModule.className'),
        description: t('moduleDetails:implementations.BmwModule.description'),
        group: t('moduleDetails:implementations.BmwModule.group'),
        FormComponent: implementations.BmwModule.FormComponent,
        create: implementations.BmwModule.create,
        getValidator: implementations.BmwModule.getValidator,
    });

    results.push({
        type: 'PorschePaymentModule',
        label: t('moduleDetails:implementations.PorschePaymentModule.className'),
        description: t('moduleDetails:implementations.PorschePaymentModule.description'),
        group: t('moduleDetails:implementations.PorschePaymentModule.group'),
        FormComponent: implementations.PorschePaymentModule.FormComponent,
        create: implementations.PorschePaymentModule.create,
        getValidator: implementations.PorschePaymentModule.getValidator,
    });

    results.push({
        type: 'FiservPaymentModule',
        label: t('moduleDetails:implementations.FiservPaymentModule.className'),
        description: t('moduleDetails:implementations.FiservPaymentModule.description'),
        group: t('moduleDetails:implementations.FiservPaymentModule.group'),
        FormComponent: implementations.FiservPaymentModule.FormComponent,
        create: implementations.FiservPaymentModule.create,
        getValidator: implementations.FiservPaymentModule.getValidator,
    });

    results.push({
        type: 'PayGatePaymentModule',
        label: t('moduleDetails:implementations.PayGatePaymentModule.className'),
        description: t('moduleDetails:implementations.PayGatePaymentModule.description'),
        group: t('moduleDetails:implementations.PayGatePaymentModule.group'),
        FormComponent: implementations.PayGatePaymentModule.FormComponent,
        create: implementations.PayGatePaymentModule.create,
        getValidator: implementations.PayGatePaymentModule.getValidator,
    });

    results.push({
        type: 'TtbPaymentModule',
        label: t('moduleDetails:implementations.TtbPaymentModule.className'),
        description: t('moduleDetails:implementations.TtbPaymentModule.description'),
        group: t('moduleDetails:implementations.TtbPaymentModule.group'),
        FormComponent: implementations.TtbPaymentModule.FormComponent,
        create: implementations.TtbPaymentModule.create,
        getValidator: implementations.TtbPaymentModule.getValidator,
    });

    if (!company.modules.some(module => module.__typename === ModuleType.MaintenanceModule)) {
        results.push({
            type: 'MaintenanceModule',
            label: t('moduleDetails:implementations.MaintenanceModule.className'),
            description: t('moduleDetails:implementations.MaintenanceModule.description'),
            group: t('moduleDetails:implementations.MaintenanceModule.group'),
            FormComponent: implementations.MaintenanceModule.FormComponent,
            create: implementations.MaintenanceModule.create,
            getValidator: implementations.MaintenanceModule.getValidator,
            initialValues: implementations.MaintenanceModule.initialValues,
        });
    }

    results.push({
        type: 'FinderVehicleManagementModule',
        label: t('moduleDetails:implementations.FinderVehicleManagementModule.className'),
        description: t('moduleDetails:implementations.FinderVehicleManagementModule.description'),
        group: t('moduleDetails:implementations.FinderVehicleManagementModule.group'),
        FormComponent: implementations.FinderVehicleManagementModule.FormComponent,
        create: implementations.FinderVehicleManagementModule.create,
        getValidator: implementations.FinderVehicleManagementModule.getValidator,
        initialValues: implementations.FinderVehicleManagementModule.initialValues,
    });

    results.push({
        type: 'FinderApplicationPublicModule',
        label: t('moduleDetails:implementations.FinderApplicationModule.className'),
        description: t('moduleDetails:implementations.FinderApplicationModule.description'),
        group: t('moduleDetails:implementations.FinderApplicationModule.group'),
        FormComponent: implementations.FinderApplicationPublicModule.FormComponent,
        create: implementations.FinderApplicationPublicModule.create,
        getValidator: implementations.FinderApplicationPublicModule.getValidator,
        initialValues: implementations.FinderApplicationPublicModule.initialValues,
    });

    results.push({
        type: 'FinderApplicationPrivateModule',
        label: t('moduleDetails:implementations.FinderApplicationPrivateModule.className'),
        description: t('moduleDetails:implementations.FinderApplicationPrivateModule.description'),
        group: t('moduleDetails:implementations.FinderApplicationPrivateModule.group'),
        FormComponent: implementations.FinderApplicationPrivateModule.FormComponent,
        create: implementations.FinderApplicationPrivateModule.create,
        getValidator: implementations.FinderApplicationPrivateModule.getValidator,
        initialValues: implementations.FinderApplicationPrivateModule.initialValues,
    });

    results.push({
        type: 'CtsModule',
        label: t('moduleDetails:implementations.CtsModule.className'),
        description: t('moduleDetails:implementations.CtsModule.description'),
        group: t('moduleDetails:implementations.CtsModule.group'),
        FormComponent: implementations.CtsModule.FormComponent,
        create: implementations.CtsModule.create,
        getValidator: implementations.CtsModule.getValidator,
        initialValues: implementations.CtsModule.initialValues,
    });

    results.push({
        type: 'PorscheMasterDataModule',
        label: t('moduleDetails:implementations.PorscheMasterDataModule.className'),
        description: t('moduleDetails:implementations.PorscheMasterDataModule.description'),
        group: t('moduleDetails:implementations.PorscheMasterDataModule.group'),
        FormComponent: implementations.PorscheMasterDataModule.FormComponent,
        create: implementations.PorscheMasterDataModule.create,
        getValidator: implementations.PorscheMasterDataModule.getValidator,
        initialValues: implementations.PorscheMasterDataModule.initialValues,
    });

    results.push({
        type: 'AutoplayModule',
        label: t('moduleDetails:implementations.AutoplayModule.className'),
        description: t('moduleDetails:implementations.AutoplayModule.description'),
        group: t('moduleDetails:implementations.AutoplayModule.group'),
        FormComponent: implementations.AutoplayModule.FormComponent,
        create: implementations.AutoplayModule.create,
        getValidator: implementations.AutoplayModule.getValidator,
        initialValues: implementations.AutoplayModule.initialValues,
    });

    results.push({
        type: 'AudiCalculationModule',
        label: t('moduleDetails:implementations.AudiCalculationModule.className'),
        description: t('moduleDetails:implementations.AudiCalculationModule.description'),
        group: t('moduleDetails:implementations.AudiCalculationModule.group'),
        FormComponent: implementations.AudiCalculationModule.FormComponent,
        create: implementations.AudiCalculationModule.create,
        getValidator: implementations.AudiCalculationModule.getValidator,
        initialValues: implementations.AudiCalculationModule.initialValues,
    });

    results.push({
        type: 'TradeInModule',
        label: t('moduleDetails:implementations.TradeInModule.className'),
        description: t('moduleDetails:implementations.TradeInModule.description'),
        group: t('moduleDetails:implementations.TradeInModule.group'),
        FormComponent: implementations.TradeInModule.FormComponent,
        create: implementations.TradeInModule.create,
        getValidator: implementations.TradeInModule.getValidator,
        initialValues: implementations.TradeInModule.initialValues,
    });

    results.push({
        type: 'AudiSalesForceModule',
        label: t('moduleDetails:implementations.AudiSalesForceModule.className'),
        description: t('moduleDetails:implementations.AudiSalesForceModule.description'),
        group: t('moduleDetails:implementations.AudiSalesForceModule.group'),
        FormComponent: implementations.AudiSalesForceModule.FormComponent,
        create: implementations.AudiSalesForceModule.create,
        getValidator: implementations.AudiSalesForceModule.getValidator,
        initialValues: implementations.AudiSalesForceModule.initialValues,
    });

    if (!company.modules.some(module => module.__typename === ModuleType.GiftVoucherModule)) {
        results.push({
            type: 'GiftVoucherModule',
            label: t('moduleDetails:implementations.GiftVoucherModule.className'),
            description: t('moduleDetails:implementations.GiftVoucherModule.description'),
            group: t('moduleDetails:implementations.GiftVoucherModule.group'),
            FormComponent: implementations.GiftVoucherModule.FormComponent,
            create: implementations.GiftVoucherModule.create,
            getValidator: implementations.GiftVoucherModule.getValidator,
            initialValues: implementations.GiftVoucherModule.initialValues,
        });
    }

    results.push({
        type: 'CapModule',
        label: t('moduleDetails:implementations.CapModule.className'),
        description: t('moduleDetails:implementations.CapModule.description'),
        group: t('moduleDetails:implementations.CapModule.group'),
        FormComponent: implementations.CapModule.FormComponent,
        create: implementations.CapModule.create,
        getValidator: implementations.CapModule.getValidator,
        initialValues: implementations.CapModule.initialValues,
    });

    return results;
};

export default getAvailableModules;
